import { Avatar } from 'components/Avatar';
import { Alpha10Percent } from 'configs/constants';
import { ISchedule, ISpeaker, ITeam } from 'configs/interfaces';
import { getColorFromCSSString, isEmptyString } from 'helper';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IRootState } from 'rematch/store';
import moment from 'moment';
import { CATEGORY3FILTERS } from 'configs/active/constants';

interface ISectionListEntry {
	item: ITeam;
	height: number;
	slotId: number;
	onClick: (team: ITeam) => void;
}

export const SectionListEntry = (props: ISectionListEntry) => {
	const { item, height, slotId, onClick } = props;

	const [remainingSeats, setRemainingSeats] = useState<number>(8);
	const [schedule, setSchedule] = useState<ISchedule | undefined>(undefined);
	const [speaker, setSpeaker] = useState<ISpeaker | undefined>(undefined);

	const teamBookings = useSelector((store: IRootState) => store.booking.teamBookings);

	useEffect(() => {
		let _schedule: typeof schedule = undefined;
		let _speaker: typeof speaker = undefined;

		if (item.maxUsers) {
			let _remaining = item.maxUsers;

			const teamBooking = teamBookings.find((e) => e.slotId === slotId && e.teamId === item.id);
			if (teamBooking) {
				_remaining = Math.max(0, _remaining - teamBooking.users.length);
			}

			setRemainingSeats(_remaining);
		}

		if (item.schedules) {
			_schedule = item.schedules.find((e) => !e.isDeleted);
		}

		if (item.speakers) {
			_speaker = item.speakers.find((e) => !e.isDeleted);
		}

		setSchedule(_schedule);
		setSpeaker(_speaker);
	}, [item, teamBookings]);

	const _renderCategories = () => {
		const tags: string[] = [];

		if (item.teamCategory3) {
			item.teamCategory3.split(',').forEach((tag) => {
				const found = CATEGORY3FILTERS.find((e) => e.key === tag);
				if (found) {
					tags.push(found.label);
				}
			});
		}

		if (tags.length > 0) {
			return (
				<div className="flex flex-wrap">
					{tags.map((tag) => {
						return (
							<div key={`${item.id}_${tag}`} className="bg-background text-xs px-2 py-px mr-1 mb-1">
								{tag}
							</div>
						);
					})}
				</div>
			);
		}

		return null;
	};

	const _renderJobtitleAndCompany = () => {
		let str = '';

		if (speaker?.jobTitle && speaker.company) {
			str = `${speaker.jobTitle} | ${speaker.company}`;
		} else if (speaker?.jobTitle) {
			str = speaker.jobTitle;
		} else if (speaker?.company) {
			str = speaker.company;
		}

		if (!isEmptyString(str)) {
			return <div className="text-xs text-lightgray text-left">{str}</div>;
		}

		return null;
	};

	return (
		<button
			className="relative w-full mb-2 flex items-center px-2"
			style={{ minHeight: height, backgroundColor: getColorFromCSSString('bg-primary-500') + Alpha10Percent }}
			onClick={() => onClick(item)}
		>
			<Avatar containerCss="my-auto" size="xs" avatar={speaker?.image?.url ?? item.image?.url} fullName={item.title} />

			<div className="flex flex-col flex-1 pl-2 xl:pl-4 items-start">
				{schedule?.startDate && schedule?.endDate && (
					<div
						className="absolute top-2 right-2 text-xs self-end px-2 py-px  bg-primary-500 text-white rounded-sm"
						style={{ backgroundColor: schedule.backgroundColor, color: schedule.textColor }}
					>{`${moment(schedule.startDate).format('HH:mm')} - ${moment(schedule.endDate).format('HH:mm')}`}</div>
				)}
				<div className="font-bold text-sm">{speaker?.title ?? item.title}</div>
				{_renderJobtitleAndCompany()}
				{_renderCategories()}
			</div>
			<div className="px-1 mt-4">
				<div className={`text-center text-2xl ${remainingSeats > 0 ? 'font-bold' : 'text-lightgray'}`}>{remainingSeats}</div>
				<div className={`text-xs leading-3 text-center ${remainingSeats === 0 ? 'text-lightgray' : ''}`}>places</div>
			</div>
		</button>
	);
};
